import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    Modal,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { Picker } from '@react-native-picker/picker';
import { theme } from '../constants/theme';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import ApiService from '../services/ApiService';
import { Customer, DairyRecord, CustomerTransaction } from '../models/types';
import { formatDate, formatCurrency, formatNumber, generateId, getISODateString } from '../utils/helpers';
import { CustomInput } from '../components/CustomInput';
import { DatePickerInput } from '../components/DatePickerInput';

const CustomerAccountScreen = ({ navigation, route }: any) => {
    const initialCustomerId = route.params?.customerId as string | undefined;

    const [customers, setCustomers] = useState<Customer[]>([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>(initialCustomerId || '');
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [dairyRecords, setDairyRecords] = useState<DairyRecord[]>([]);
    const [balance, setBalance] = useState({ totalCredit: 0, totalPaid: 0, balance: 0, advance: 0 });
    const [loading, setLoading] = useState(false);

    // Payment modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(getISODateString(new Date()));
    const [paymentNotes, setPaymentNotes] = useState('');
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const loadCustomers = async () => {
        try {
            const allCustomers = await ApiService.getCustomers();
            setCustomers(allCustomers);
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    const loadCustomerData = async (customerId: string) => {
        if (!customerId) {
            setSelectedCustomer(null);
            setDairyRecords([]);
            setBalance({ totalCredit: 0, totalPaid: 0, balance: 0, advance: 0 });
            return;
        }

        setLoading(true);
        try {
            const customer = await ApiService.getCustomerById(customerId);
            setSelectedCustomer(customer);

            const records = await ApiService.getCustomerDairyRecords(customerId);
            setDairyRecords(records);

            const balanceData = await ApiService.getCustomerBalance(customerId);
            setBalance(balanceData);
        } catch (error) {
            console.error('Error loading customer data:', error);
        } finally {
            setLoading(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            loadCustomers();
        }, [])
    );

    useFocusEffect(
        useCallback(() => {
            if (selectedCustomerId) {
                loadCustomerData(selectedCustomerId);
            }
        }, [selectedCustomerId])
    );

    // Set initial customer when passed via route
    React.useEffect(() => {
        if (initialCustomerId && !selectedCustomerId) {
            setSelectedCustomerId(initialCustomerId);
        }
    }, [initialCustomerId]);

    const handleAddPayment = async () => {
        if (!selectedCustomerId || !paymentAmount) {
            setModal({ visible: true, type: 'error', title: 'Error', message: 'Please enter a payment amount' });
            return;
        }

        const amount = parseFloat(paymentAmount);
        if (isNaN(amount) || amount <= 0) {
            setModal({ visible: true, type: 'error', title: 'Error', message: 'Please enter a valid payment amount' });
            return;
        }

        setLoading(true);
        try {
            await ApiService.addCustomerTransaction(selectedCustomerId, {
                transactionType: 'payment',
                amount,
                date: paymentDate,
                notes: paymentNotes || undefined,
            });

            // Reload data
            await loadCustomerData(selectedCustomerId);

            // Reset modal
            setShowPaymentModal(false);
            setPaymentAmount('');
            setPaymentNotes('');
            setPaymentDate(getISODateString(new Date()));

            setModal({ visible: true, type: 'success', title: 'Success', message: 'Payment recorded successfully' });
        } catch (error) {
            console.error('Error adding payment:', error);
            setModal({ visible: true, type: 'error', title: 'Error', message: 'Failed to record payment' });
        } finally {
            setLoading(false);
        }
    };

    const renderRecordItem = ({ item }: { item: DairyRecord }) => {
        const getStatusColor = () => {
            switch (item.paymentStatus) {
                case 'paid': return theme.colors.success;
                case 'partial': return theme.colors.warning;
                case 'pending': return theme.colors.error;
                default: return theme.colors.textSecondary;
            }
        };

        return (
            <View style={styles.recordCard}>
                <View style={styles.recordHeader}>
                    <View style={styles.recordDateRow}>
                        <Icon name="calendar" size={14} color={theme.colors.textSecondary} />
                        <Text style={styles.recordDate}>{formatDate(item.date)}</Text>
                    </View>
                    {item.paymentStatus && (
                        <View style={[styles.statusBadge, { backgroundColor: getStatusColor() + '20' }]}>
                            <Text style={[styles.statusText, { color: getStatusColor() }]}>
                                {item.paymentStatus.charAt(0).toUpperCase() + item.paymentStatus.slice(1)}
                            </Text>
                        </View>
                    )}
                </View>

                <View style={styles.recordDetails}>
                    <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Quantity</Text>
                        <Text style={styles.detailValue}>{formatNumber(item.quantity)} L</Text>
                    </View>
                    <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Total Amount</Text>
                        <Text style={styles.detailValue}>{formatCurrency(item.totalAmount || 0)}</Text>
                    </View>
                    <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Amount Paid</Text>
                        <Text style={[styles.detailValue, { color: theme.colors.success }]}>
                            {formatCurrency(item.amountPaid || 0)}
                        </Text>
                    </View>
                    {(item.pendingAmount ?? 0) > 0 && (
                        <View style={styles.detailRow}>
                            <Text style={styles.detailLabel}>Pending</Text>
                            <Text style={[styles.detailValue, { color: theme.colors.error }]}>
                                {formatCurrency(item.pendingAmount || 0)}
                            </Text>
                        </View>
                    )}
                </View>

                <Text style={styles.recordType}>
                    <Icon
                        name={item.recordType === 'sale' ? 'cart-arrow-up' : 'cart-arrow-down'}
                        size={12}
                        color={theme.colors.textSecondary}
                    />
                    {' '}{item.recordType.charAt(0).toUpperCase() + item.recordType.slice(1)}
                </Text>
            </View>
        );
    };

    const renderEmpty = () => (
        <View style={styles.emptyContainer}>
            <Icon name="file-document-outline" size={48} color={theme.colors.textLight} />
            <Text style={styles.emptyText}>
                {selectedCustomerId ? 'No transactions found' : 'Select a customer to view transactions'}
            </Text>
        </View>
    );

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            {/* Header */}
            <View style={styles.header}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
                    <Icon name="arrow-left" size={24} color="#fff" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Customer Account</Text>
                <View style={{ width: 40 }} />
            </View>

            {/* Customer Selector */}
            <View style={styles.selectorContainer}>
                <Text style={styles.selectorLabel}>Select Customer</Text>
                <View style={styles.pickerWrapper}>
                    <Picker
                        selectedValue={selectedCustomerId}
                        onValueChange={(value) => setSelectedCustomerId(value)}
                        style={styles.picker}
                    >
                        <Picker.Item label="-- Select a Customer --" value="" />
                        {customers.map((customer) => (
                            <Picker.Item
                                key={customer.id}
                                label={`${customer.name} (${customer.customerType === 'sale' ? 'Buyer' : 'Supplier'})`}
                                value={customer.id}
                            />
                        ))}
                    </Picker>
                </View>
            </View>

            {selectedCustomer && (
                <>
                    {/* Summary Cards */}
                    <View style={styles.summaryContainer}>
                        <View style={styles.summaryRow}>
                            <View style={styles.summaryCard}>
                                <Text style={styles.summaryLabel}>Total Amount</Text>
                                <Text style={styles.summaryValue}>{formatCurrency(balance.totalCredit)}</Text>
                            </View>
                            <View style={styles.summaryCard}>
                                <Text style={styles.summaryLabel}>Amount Paid</Text>
                                <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
                                    {formatCurrency(balance.totalPaid)}
                                </Text>
                            </View>
                        </View>
                        <View style={styles.summaryRow}>
                            <View style={[styles.summaryCard, balance.balance > 0 && styles.pendingCard]}>
                                <Text style={styles.summaryLabel}>Pending Balance</Text>
                                <Text style={[styles.summaryValue, balance.balance > 0 && { color: theme.colors.error }]}>
                                    {formatCurrency(balance.balance)}
                                </Text>
                            </View>
                            <View style={[styles.summaryCard, balance.advance > 0 && styles.advanceCard]}>
                                <Text style={styles.summaryLabel}>Advance</Text>
                                <Text style={[styles.summaryValue, balance.advance > 0 && { color: theme.colors.success }]}>
                                    {formatCurrency(balance.advance)}
                                </Text>
                            </View>
                        </View>
                    </View>

                    {/* Add Payment Button */}
                    {balance.balance > 0 && (
                        <TouchableOpacity
                            style={styles.addPaymentBtn}
                            onPress={() => setShowPaymentModal(true)}
                        >
                            <Icon name="cash-plus" size={20} color="#fff" />
                            <Text style={styles.addPaymentBtnText}>Add Payment</Text>
                        </TouchableOpacity>
                    )}

                    {/* Transactions Header */}
                    <View style={styles.sectionHeader}>
                        <Text style={styles.sectionTitle}>Transactions ({dairyRecords.length})</Text>
                    </View>
                </>
            )}

            {/* Transactions List */}
            <FlashList
                data={dairyRecords}
                keyExtractor={(item) => item.id}
                renderItem={renderRecordItem}
                contentContainerStyle={styles.listContent}
                ListEmptyComponent={renderEmpty}
                refreshing={loading}
                onRefresh={() => selectedCustomerId && loadCustomerData(selectedCustomerId)}
            />

            {/* Payment Modal */}
            <Modal
                visible={showPaymentModal}
                animationType="slide"
                transparent={true}
                onRequestClose={() => setShowPaymentModal(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>Record Payment</Text>
                            <TouchableOpacity onPress={() => setShowPaymentModal(false)}>
                                <Icon name="close" size={24} color={theme.colors.text} />
                            </TouchableOpacity>
                        </View>

                        <Text style={styles.pendingInfo}>
                            Pending Balance: <Text style={styles.pendingAmount}>{formatCurrency(balance.balance)}</Text>
                        </Text>

                        <DatePickerInput
                            label="Payment Date"
                            value={paymentDate}
                            onChangeDate={setPaymentDate}
                            placeholder="Select date"
                        />

                        <CustomInput
                            label="Amount *"
                            value={paymentAmount}
                            onChangeText={setPaymentAmount}
                            icon="currency-inr"
                            keyboardType="decimal-pad"
                            placeholder="Enter payment amount"
                        />

                        <CustomInput
                            label="Notes"
                            value={paymentNotes}
                            onChangeText={setPaymentNotes}
                            icon="note-text"
                            placeholder="Payment reference or notes"
                        />

                        <View style={styles.modalButtons}>
                            <TouchableOpacity
                                style={styles.modalCancelBtn}
                                onPress={() => setShowPaymentModal(false)}
                            >
                                <Text style={styles.modalCancelText}>Cancel</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.modalSaveBtn}
                                onPress={handleAddPayment}
                            >
                                <Text style={styles.modalSaveText}>Save Payment</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                confirmText="OK"
            />

            <LoadingOverlay visible={loading} />
        </SafeAreaView >
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        backgroundColor: theme.colors.primary,
    },
    backButton: {
        padding: 8,
    },
    headerTitle: {
        ...theme.typography.h5,
        color: '#fff',
    },
    selectorContainer: {
        padding: theme.spacing.md,
        backgroundColor: theme.colors.surface,
    },
    selectorLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.xs,
    },
    pickerWrapper: {
        borderWidth: 1,
        borderColor: theme.colors.border,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.background,
    },
    picker: {
        height: 50,
    },
    summaryContainer: {
        padding: theme.spacing.md,
    },
    summaryRow: {
        flexDirection: 'row',
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.sm,
    },
    summaryCard: {
        flex: 1,
        backgroundColor: theme.colors.surface,
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        ...theme.shadows.sm,
    },
    pendingCard: {
        backgroundColor: '#FFEBEE',
    },
    advanceCard: {
        backgroundColor: '#E8F5E9',
    },
    summaryLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    summaryValue: {
        ...theme.typography.h5,
        color: theme.colors.text,
        marginTop: 4,
    },
    addPaymentBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: theme.spacing.md,
        marginBottom: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        backgroundColor: theme.colors.success,
        borderRadius: theme.borderRadius.md,
        gap: 8,
    },
    addPaymentBtnText: {
        ...theme.typography.button,
        color: '#fff',
    },
    sectionHeader: {
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
    },
    sectionTitle: {
        ...theme.typography.h6,
        color: theme.colors.text,
    },
    listContent: {
        paddingHorizontal: theme.spacing.md,
        paddingBottom: theme.spacing.xl,
    },
    recordCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        ...theme.shadows.sm,
    },
    recordHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    recordDateRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    recordDate: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
    },
    statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: theme.borderRadius.sm,
    },
    statusText: {
        ...theme.typography.caption,
        fontWeight: '600',
    },
    recordDetails: {
        borderTopWidth: 1,
        borderTopColor: theme.colors.divider,
        paddingTop: theme.spacing.sm,
    },
    detailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    detailLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
    },
    detailValue: {
        ...theme.typography.body2,
        fontWeight: '600',
        color: theme.colors.text,
    },
    recordType: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.sm,
    },
    emptyContainer: {
        alignItems: 'center',
        paddingVertical: theme.spacing.xxl,
    },
    emptyText: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.sm,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'flex-end',
    },
    modalContent: {
        backgroundColor: theme.colors.surface,
        borderTopLeftRadius: theme.borderRadius.xl,
        borderTopRightRadius: theme.borderRadius.xl,
        padding: theme.spacing.lg,
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.md,
    },
    modalTitle: {
        ...theme.typography.h5,
        color: theme.colors.text,
    },
    pendingInfo: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.md,
    },
    pendingAmount: {
        fontWeight: '700',
        color: theme.colors.error,
    },
    modalButtons: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.md,
    },
    modalCancelBtn: {
        flex: 1,
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
        alignItems: 'center',
    },
    modalCancelText: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
    },
    modalSaveBtn: {
        flex: 2,
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.success,
        alignItems: 'center',
    },
    modalSaveText: {
        ...theme.typography.button,
        color: '#fff',
    },
});

export default CustomerAccountScreen;
